import { Component, OnInit, ViewChild } from '@angular/core';
import { PagedResultDto } from '@abp/ng.core';
import { Column } from '../shared/components/metro-table/metro-table.component';
import { GetWineDto, WineDto } from '../proxy/wines/models';
import { WineFormComponent } from './create-edit-wine/wine-form.component';
import { ConfirmationModalService } from '../shared/services/confirmation-modal.service';
import { WineService } from '@proxy/wines';
import { Router } from '@angular/router';
import { ColppyStockSyncService } from '@proxy/colppy';
import { NotificationService } from '../shared/services/notification.service';
import { finalize, take } from 'rxjs';
import { APP_ICONS } from '../shared/icons/app-icons';

@Component({
  selector: 'app-wines',
  templateUrl: './wines.component.html',
  standalone: false,
})
export class WinesComponent implements OnInit {
  // winepassport#379 (metro-table con break-words) — "Stock"/"Stock ($)" al 5% eran
  // demasiado angostos para el propio header: el texto rompía letra por letra. Rebalanceado.
  columns: Column[] = [
    { name: 'name', label: '::Name', width: '18%' },
    // winepassport#385 — "País" wrappeaba y duplicaba la altura de fila con países comunes
    // (España, Francia, Italia). +4% sacado de "Tipo", que tenía margen de sobra.
    { name: 'wineryCountry', label: '::Country', width: '14%', textColor: 'text-metro-blue-secondary' },
    { name: 'varietal', label: '::Type', width: '20%', textColor: 'text-metro-blue-secondary' },
    { name: 'price', label: '::Price', type: 'price', width: '13%', textColor: 'text-metro-blue-secondary' },
    { name: 'stock', label: '::Stock', width: '15%', textColor: 'text-metro-blue-secondary' },
    { name: 'stockValue', label: '::Stock ($)', type: 'price', width: '20%', textColor: 'text-metro-blue-secondary' },
  ];

  actions: any[] = [
    { name: 'edit', label: '::Edit', handler: (row: WineDto) => this.openEditModal(row) },
    { name: 'delete', label: '::Delete', handler: (row: WineDto) => this.deleteWine(row) },
  ];

  data: PagedResultDto<WineDto> = { items: [], totalCount: 0 };
  searchWine = {} as GetWineDto;
  isLoading = true;
  isExporting = false;

  showWineModal = false;
  selectedWine: WineDto | null = null;
  currentPage: number = 1;
  maxResultCount: number = 10;

  totalStockValue: number = 0;

  excelIcon = APP_ICONS.excel;

  @ViewChild(WineFormComponent) wineForm?: WineFormComponent;

  constructor(
    private wineService: WineService,
    private confirmation: ConfirmationModalService,
    private router: Router,
    private colppyStockSyncService: ColppyStockSyncService,
    private notificationService: NotificationService

  ) { }

  ngOnInit(): void {
    this.loadWines();
  }

  loadWines() {
    this.isLoading = true;
    const skipCount = Math.max(0, (this.currentPage - 1) * this.maxResultCount);

    const params = {
      ...this.searchWine,
      skipCount: skipCount,
      maxResultCount: this.maxResultCount
    };

    this.wineService.getListWithTotal(params).subscribe((result) => {
      this.data.items = result.items;
      this.data.totalCount = result.totalCount;

      this.totalStockValue = result.totalStockValue;

      // Actualizar el total en la columna stockValue
      const stockValueColumn = this.columns.find(col => col.name === 'stockValue');
      if (stockValueColumn) {
        stockValueColumn.total = this.totalStockValue;
      }
      this.isLoading = false;
    });
  }


  onSearch(searchTerm: string) {
    this.searchWine.keyword = searchTerm;
    this.currentPage = 1;
    this.loadWines();
  }

  onClearSearch() {
    this.searchWine = {} as GetWineDto;
    this.currentPage = 1;
    this.loadWines();
  }

  handleWineSaved(wine: WineDto) {
    this.loadWines();
    this.showWineModal = false;
  }

  openCreateModal() {
    this.router.navigate(['/wines/create']);
  }

  openEditModal(wine: WineDto) {
    this.router.navigate(['/wines/edit', wine.id]);
  }

  deleteWine(wine: WineDto) {
    this.confirmation.confirm({
      title: '::AreYouSureToDelete',
      message: '::ThisActionCannotBeUndone',
      type: 'danger',
      showCancelButton: true,
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.wineService.delete(wine.id).subscribe(() => this.loadWines());
    });
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadWines();
  }

  generateExcel() {
    this.isExporting = true;

    this.wineService.generateExcel({ responseType: 'blob' } as any)
      .pipe(
        take(1),
        finalize(() => this.isExporting = false)
      )
      .subscribe(blob => {
        const fileName = 'Vinos.xlsx';
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.click();

        window.URL.revokeObjectURL(downloadUrl);
      });
  }
 
  updateStock() {
    this.colppyStockSyncService.syncStockWithColppy().subscribe({
      next: res => {
        this.notificationService.success('', '::UpdatedStock');
        this.loadWines();
      },
      error: err => {
        console.error(err);
      }
    });
  }
}
